import { Link, usePage } from "@inertiajs/react";
import { useFlashToast } from "@/hooks/useFlashToast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Beer,
  BarChart3,
  Globe,
  Library,
  Search,
  ShieldCheck,
  Tags,
  Users,
  Warehouse,
} from "lucide-react";

type SharedProps = {
  currentUser: {
    id: number;
    username: string;
  } | null;
};

const features = [
  {
    icon: Library,
    title: "Catalog Everything",
    description:
      "Track every bottle in your collection with detailed entries — name, producer, category, country of origin, and size.",
  },
  {
    icon: Tags,
    title: "Organize by Category",
    description:
      "Create custom categories like Whiskey, Tequila, or Amaro to keep your collection neatly organized.",
  },
  {
    icon: Warehouse,
    title: "Track Producers",
    description:
      "Maintain a directory of distilleries and producers with full address details and country information.",
  },
  {
    icon: Globe,
    title: "Worldwide Coverage",
    description:
      "Built-in country database so you can trace every spirit back to where it was made.",
  },
  {
    icon: Search,
    title: "Search & Filter",
    description:
      "Quickly find what you're looking for with full-text filtering, column sorting, and paginated results.",
  },
  {
    icon: Users,
    title: "Team Ready",
    description:
      "Organization-scoped data means your team sees only what belongs to them — perfect for bars, shops, and clubs.",
  },
  {
    icon: BarChart3,
    title: "At-a-Glance Stats",
    description:
      "See your collection totals, browse by category, or drill into a specific producer's lineup.",
  },
  {
    icon: ShieldCheck,
    title: "Secure by Default",
    description:
      "Passkey-based passwordless login, encrypted sessions, and role-scoped access keep your data safe.",
  },
];

export default function Home() {
  const { currentUser, flash } = usePage<{ props: SharedProps }>().props as unknown as SharedProps & { flash?: { notice?: string; alert?: string } };
  useFlashToast(flash);

  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-950/90 via-stone-900/95 to-stone-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-amber-500/10 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23d97706%22%20fill-opacity%3D%220.04%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-50" />

        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8 lg:py-40">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-amber-500/20 bg-amber-500/10 px-4 py-1.5 text-sm text-amber-300">
              <Beer className="size-4" />
              The spirit collection manager
            </div>

            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Your collection,{" "}
              <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                organized
              </span>
            </h1>

            <p className="mt-6 text-lg leading-relaxed text-stone-300 sm:text-xl">
              BoozeHound helps you catalog, organize, and explore your spirit
              collection. Track every bottle from producer to pour — whether
              you're managing a home bar, a retail shop, or a tasting club.
            </p>

            <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              {currentUser ? (
                <>
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white" asChild>
                    <Link href="/boozes">Browse Collection</Link>
                  </Button>
                  <Button size="lg" variant="outline" className="border-stone-700 bg-stone-900 text-stone-200 hover:bg-stone-800 hover:text-white" asChild>
                    <Link href="/boozes/new">Add a Bottle</Link>
                  </Button>
                </>
              ) : (
                <>
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white" asChild>
                    <Link href="/session/new">Get Started</Link>
                  </Button>
                  <Button size="lg" variant="outline" className="border-stone-700 bg-stone-900 text-stone-200 hover:bg-stone-800 hover:text-white" asChild>
                    <a href="#features">Learn More</a>
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="mx-auto mt-16 grid max-w-lg grid-cols-3 gap-8 text-center sm:max-w-xl">
            {[
              { value: "250+", label: "Countries" },
              { value: "∞", label: "Bottles" },
              { value: "100%", label: "Organized" },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="text-3xl font-bold text-amber-400 sm:text-4xl">{stat.value}</p>
                <p className="mt-1 text-sm text-stone-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Everything you need to manage your spirits
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              From a single bottle of bourbon to a warehouse of rare finds —
              BoozeHound scales with your collection.
            </p>
          </div>

          <div className="mx-auto mt-16 grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((feature) => (
              <Card key={feature.title} className="border-border/50 bg-card/50">
                <CardHeader className="pb-3">
                  <div className="mb-2 flex size-10 items-center justify-center rounded-lg bg-amber-500/10">
                    <feature.icon className="size-5 text-amber-600" />
                  </div>
                  <CardTitle className="text-base">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t bg-muted/30 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Ready to take stock?
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Stop tracking your collection in spreadsheets. BoozeHound gives
              you a purpose-built tool to catalog, search, and manage every
              bottle you own.
            </p>
            <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              {currentUser ? (
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white" asChild>
                  <Link href="/boozes">Go to Collection</Link>
                </Button>
              ) : (
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white" asChild>
                  <Link href="/session/new">Sign In to Get Started</Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t py-8">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Beer className="size-4" />
            BoozeHound
          </div>
          <p className="text-sm text-muted-foreground">
            Drink responsibly. Catalog obsessively.
          </p>
        </div>
      </footer>
    </div>
  );
}
